import {
	Box,
	ButtonBase,
	Divider,
	Stack,
	Typography,
	useTheme,
} from "@mui/material";
import { CheckCheck } from "lucide-react";
import { useEffect, useState } from "react";
import { NotificationsKeys } from "web_myplann/Api/Notifications/notificatioKeys";
import { useNotificationMarkAllAsReadMutation } from "web_myplann/Api/Notifications/UseNotificationMarkAllAsReadMutation";
import { useNotificationsGroupedQuery } from "web_myplann/Api/Notifications/useNotificationsGroupedQuery";
import { NotificationListItem } from "web_myplann/Dashboard/Components/NotificationListItem";
import { Routing } from "web_myplann/Infrastructure/Routing/Routing";
import {
	Resources,
	useResource,
} from "web_myplann/Infrastructure/Translations/Resources";
import { MyPlannTabs } from "web_myplann/Shared/Components/MyPlannTabs";
import { NativePullToRefresh } from "web_myplann/Shared/Components/NativePullToRefresh";
import { MyPlannLayout } from "web_myplann/Shared/Header/MyPlannLayout";
import NavigationHeader from "web_myplann/Shared/Header/NavigationHeader";
import useIntersectionObserver from "web_myplann/Shared/Hooks/useIntersectionObserver";
import { RowSkeletonLoader } from "web_myplann/Shared/RowSkeletonLoader";

const STORAGE_KEY = "notification-tab";

interface TabPanelProps {
	children?: React.ReactNode;
	index: number;
	value: number;
}

function CustomTabPanel(props: TabPanelProps) {
	const { children, value, index, ...other } = props;

	return (
		<div
			role="tabpanel"
			hidden={value !== index}
			id={`simple-tabpanel-${index}`}
			aria-labelledby={`simple-tab-${index}`}
			{...other}
		>
			{value === index && <Box>{children}</Box>}
		</div>
	);
}

export const NotificationPage = () => {
	const { t } = useResource();
	const { palette } = useTheme();

	const {
		data: unreadPages,
		fetchNextPage: fetchNextUnread,
		hasNextPage: hasNextUnread,
		isFetchingNextPage: isFetchingNextUnread,
	} = useNotificationsGroupedQuery(true);

	const {
		data: allPages,
		fetchNextPage: fetchNextAll,
		hasNextPage: hasNextAll,
		isFetchingNextPage: isFetchingNextAll,
	} = useNotificationsGroupedQuery(false);

	const { mutate: markAllAsRead } = useNotificationMarkAllAsReadMutation();

	const [value, setValue] = useState(
		Number(localStorage.getItem(STORAGE_KEY)) || 0,
	);

	const [hasScrolled, setHasScrolled] = useState(false);
	useEffect(() => {
		const onScroll = () => {
			if (
				!hasScrolled &&
				(window.scrollY || document.documentElement.scrollTop) > 0
			) {
				setHasScrolled(true);
			}
		};
		window.addEventListener("scroll", onScroll, { passive: true });
		return () => window.removeEventListener("scroll", onScroll);
	}, [hasScrolled]);

	const handleChange = (_event: React.SyntheticEvent, newValue: number) => {
		setValue(newValue);
		localStorage.setItem(STORAGE_KEY, String(newValue));
	};

	const dedupeById = <T extends { id: number }>(arr: T[]) =>
		Array.from(new Map(arr.map((i) => [i.id, i])).values());

	const unreadNotifications = dedupeById(
		unreadPages?.pages?.flatMap((p) => p?.data ?? []) ?? [],
	);
	const allNotifications = dedupeById(
		allPages?.pages?.flatMap((p) => p?.data ?? []) ?? [],
	);

	const unreadCount = unreadPages?.pages?.[0]?.meta?.unreadCount ?? 0;

	const newestUnreadId =
		unreadNotifications.length > 0
			? unreadNotifications.reduce((latest, n) =>
					Date.parse(n.createdAt) > Date.parse(latest.createdAt) ? n : latest,
				).id
			: undefined;

	const lastUnreadRef = useIntersectionObserver(
		hasNextUnread && !isFetchingNextUnread && hasScrolled && value === 0
			? () => fetchNextUnread()
			: undefined,
		unreadNotifications.length,
		undefined,
		!!hasNextUnread && !isFetchingNextUnread && hasScrolled && value === 0,
	);

	const lastAllRef = useIntersectionObserver(
		hasNextAll && !isFetchingNextAll && hasScrolled && value === 1
			? () => fetchNextAll()
			: undefined,
		allNotifications.length,
		undefined,
		!!hasNextAll && !isFetchingNextAll && hasScrolled && value === 1,
	);

	return (
		<MyPlannLayout
			topBar={
				<NavigationHeader
					title={t(Resources.Notifications.title)}
					defaultRoute={Routing.Dashboard.path()}
					bottomComponent={
						<MyPlannTabs
							value={value}
							onChange={handleChange}
							tabs={[
								{
									id: "unread",
									label: `${t(Resources.Notifications.Tabs.unread)} (${unreadCount})`,
								},
								{
									id: "all",
									label: t(Resources.Notifications.Tabs.all),
								},
							]}
						/>
					}
					disableBottomPadding
				/>
			}
		>
			<NativePullToRefresh
				queryKeys={[
					NotificationsKeys.count(),
					NotificationsKeys.infiniteGroupedList(true),
					NotificationsKeys.infiniteGroupedList(false),
				]}
				spinnerOffset={-25}
			>
				<Box pt={1.6}>
					<CustomTabPanel value={value} index={0}>
						{unreadCount > 0 ? (
							<>
								<Stack display="flex" alignItems="flex-end">
									<ButtonBase
										sx={{
											gap: 0.8,
											pb: 1.6,
										}}
										onClick={() => markAllAsRead(newestUnreadId ?? 0)}
									>
										<CheckCheck size={16} color={palette.primary.main} />
										<Typography variant="body2" color={palette.primary.main}>
											{t(Resources.Notifications.readAll)}
										</Typography>
									</ButtonBase>
								</Stack>
								{unreadNotifications.map((notification) => (
									<Stack key={notification.id}>
										<NotificationListItem {...notification} />
										<Box py={1.6}>
											<Divider />
										</Box>
									</Stack>
								))}
								<Box ref={lastUnreadRef} sx={{ height: 1 }} />
							</>
						) : (
							<Stack
								height="85vh"
								justifyContent="center"
								alignItems="center"
								gap={1.6}
								padding="0 64px"
							>
								<RowSkeletonLoader disableAnimation />
								<RowSkeletonLoader disableAnimation />
								<RowSkeletonLoader disableAnimation />
								<Typography
									fontSize={16}
									fontWeight={600}
									color={palette.grey[700]}
								>
									{t(Resources.Activity.List.NoContent)}
								</Typography>
								<Typography
									fontSize={12}
									fontWeight={400}
									color={palette.grey[500]}
									align="center"
								>
									{t(Resources.Notifications.noContent)}
								</Typography>
							</Stack>
						)}
					</CustomTabPanel>
					<CustomTabPanel value={value} index={1}>
						<Stack pt={1}>
							{allNotifications.map((notification) => (
								<Stack key={notification.id}>
									<NotificationListItem {...notification} />
									<Box py={1.6}>
										<Divider />
									</Box>
								</Stack>
							))}
							<Box ref={lastAllRef} sx={{ height: 1 }} />
						</Stack>
					</CustomTabPanel>
				</Box>
			</NativePullToRefresh>
		</MyPlannLayout>
	);
};
